import { useState } from "react";
import { Link } from "wouter";
import {
  useCorpus,
  useCorpusBinding,
  useDeployService,
  useProjectGovernancePolicy,
  useServices,
} from "../../api/queries";
import type { Project, Service } from "../../api/types";
import { EmptyState, ErrorState, LoadingState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { ProvenanceRungPill, ServiceStatusPill } from "../../components/StatePill";
import { CORPUS_TEACHING_SUBTITLE, shortCommit } from "../corpus/CorpusPage";
import { useToast } from "../../toast/ToastContext";
import "./projectOverview.css";

/** A service that hasn't been deployed at least once yet — mirrors the server's own
 * `created` status verbatim (`StatePill.tsx`'s `SERVICE_STATUS_META`), never a guess
 * derived from placements or desired count (A-10). */
function notYetDeployed(service: Service): boolean {
  return service.status === "created";
}

/**
 * The Overview tab (IA slice 3, ui-information-architecture.md §3 / the approved
 * ProjectReady mock): the "applied-corpus moment" — a real corpus card, a real
 * next-step card, and a compact services list with inline Deploy. Every fact here comes
 * straight off `corpussvc`/`corpusbindingsvc`/`projectsvc`/`servicesvc` responses; a
 * project that has never applied a corpus, or has no services yet, gets the honest empty
 * state instead of a fabricated "applied" story (A-10) — see each card's own comment for
 * exactly which fields this omits and why.
 */
export function ProjectOverviewTab({ project, canManage }: { project: Project; canManage: boolean }) {
  const {
    data: corpus,
    isPending: corpusPending,
    isError: corpusIsError,
    error: corpusError,
    refetch: refetchCorpus,
  } = useCorpus(project.id);
  const { data: binding } = useCorpusBinding(project.id, { enabled: canManage });
  const { data: policy } = useProjectGovernancePolicy(project.id);
  const {
    data: servicesPage,
    isPending: servicesPending,
    isError: servicesIsError,
    error: servicesError,
    refetch: refetchServices,
  } = useServices(project.id);

  const synced = Boolean(corpus?.sourceCommit);
  const services = servicesPage?.items ?? [];
  const undeployedCount = services.filter(notYetDeployed).length;

  return (
    <div className="project-overview">
      <div className="project-overview__cards">
        <section className="project-overview__card" aria-label="Corpus">
          <h3 className="project-overview__card-label">Corpus</h3>
          <p className="project-overview__muted">{CORPUS_TEACHING_SUBTITLE}</p>
          {corpusPending ? (
            <LoadingState label="Loading corpus…" />
          ) : corpusIsError ? (
            <ErrorState
              description={corpusError instanceof Error ? corpusError.message : "Couldn't load this project's corpus."}
              onRetry={refetchCorpus}
            />
          ) : synced && corpus ? (
            <div className="project-overview__corpus-applied">
              <p>
                Applied <code title={corpus.sourceCommit}>{shortCommit(corpus.sourceCommit)}</code>
                {canManage && binding && (
                  <>
                    {" "}
                    <ProvenanceRungPill rung={binding.expectedRung} />
                  </>
                )}
              </p>
              {corpus.syncedAt && (
                <p className="project-overview__muted">
                  Last synced <time dateTime={corpus.syncedAt}>{new Date(corpus.syncedAt).toLocaleString()}</time>
                </p>
              )}
              {policy?.governanceConfigured && (
                <p className="project-overview__muted">
                  {policy.predicates.length} governance rule{policy.predicates.length === 1 ? "" : "s"} declared —{" "}
                  <Link to="/govern/decisions">see it in Govern</Link>
                </p>
              )}
              <Link to={`/projects/${project.id}/corpus`} className="button button--secondary">
                View corpus
              </Link>
            </div>
          ) : (
            <EmptyState
              title="No corpus applied yet"
              description={
                canManage
                  ? "Bind a repository and develop this project's vision, roadmap, and decisions from git."
                  : "An operator or admin can bind a repository to start syncing this project's corpus."
              }
              action={
                <Link to={`/projects/${project.id}/corpus`} className="button button--secondary">
                  Go to Corpus
                </Link>
              }
            />
          )}
        </section>

        <NextStepCard project={project} synced={synced} services={services} undeployedCount={undeployedCount} />
      </div>

      <section aria-label="Services">
        <h3 className="project-overview__card-label">Services</h3>
        {servicesPending ? (
          <LoadingState label="Loading services…" />
        ) : servicesIsError ? (
          <ErrorState
            description={servicesError instanceof Error ? servicesError.message : "Couldn't load services."}
            onRetry={refetchServices}
          />
        ) : services.length === 0 ? (
          <EmptyState
            title="No services in this project yet"
            description="Add one from the Services tab — import a compose file or define an image by hand."
            action={
              <Link to={`/projects/${project.id}/services`} className="button button--secondary">
                Go to Services
              </Link>
            }
          />
        ) : (
          <ServiceQuickList project={project} services={services} canManage={canManage} />
        )}
      </section>
    </div>
  );
}

function NextStepCard({
  project,
  synced,
  services,
  undeployedCount,
}: {
  project: Project;
  synced: boolean;
  services: Service[];
  undeployedCount: number;
}) {
  let body: { text: string; action?: { label: string; href: string } };
  if (!synced) {
    body = {
      text: `${project.name}'s corpus hasn't been applied yet.`,
      action: { label: "Develop it", href: `/projects/${project.id}/sessions` },
    };
  } else if (services.length === 0) {
    body = {
      text: `${project.name}'s corpus is applied, but it has no services defined yet.`,
      action: { label: "Add a service", href: `/projects/${project.id}/services` },
    };
  } else if (undeployedCount > 0) {
    body = {
      text: `${project.name}'s ${services.length === 1 ? "service is" : `${services.length} services are`} defined but ${
        undeployedCount === services.length ? "not yet running" : `${undeployedCount} not yet running`
      }.`,
      action: { label: "Go to Services", href: `/projects/${project.id}/services` },
    };
  } else {
    body = {
      text: `Every service in ${project.name} has been deployed at least once.`,
      action: { label: "View services", href: `/projects/${project.id}/services` },
    };
  }

  return (
    <section className="project-overview__card" aria-label="Next step">
      <h3 className="project-overview__card-label">Next step</h3>
      <p>{body.text}</p>
      {body.action && (
        <Link to={body.action.href} className="button button--secondary">
          {body.action.label}
        </Link>
      )}
    </section>
  );
}

/** A compact, read-mostly services list for the Overview tab — the full manageable table
 * (create/import, desired count, placements, updated-at) lives on the Services tab; this
 * is deliberately narrower (name, status, image, Deploy), matching the approved
 * ProjectReady mock. Deploy fires the SAME real mutation `ServiceDetailPage` uses — this
 * is not a second, lesser deploy action. */
function ServiceQuickList({
  project,
  services,
  canManage,
}: {
  project: Project;
  services: Service[];
  canManage: boolean;
}) {
  const { pushToast } = useToast();
  const deployMutation = useDeployService(project.id);
  const [deployingId, setDeployingId] = useState<string | null>(null);

  function handleDeploy(service: Service) {
    setDeployingId(service.id);
    deployMutation.mutate(
      { id: service.id },
      {
        onSuccess: () => pushToast({ title: `Deployed "${service.name}".`, tone: "success" }),
        onError: (err) => {
          if (err.status === 409) {
            pushToast({ title: "Deploy not available yet", description: err.message, tone: "info" });
            return;
          }
          pushToast({
            title: `Couldn't deploy "${service.name}"`,
            description: err.message || "Try again in a moment.",
            tone: "error",
          });
        },
        onSettled: () => setDeployingId(null),
      },
    );
  }

  return (
    <ul className="project-overview__services">
      {services.map((service) => (
        <li key={service.id} className="project-overview__service-row">
          <ServiceStatusPill status={service.status} />
          <Link
            to={`/projects/${project.id}/services/${service.id}`}
            className="project-overview__service-name data-table__primary-link"
          >
            {service.name}
          </Link>
          <span className="project-overview__service-image">{service.image || "—"}</span>
          {canManage && (
            <Button
              variant="secondary"
              disabled={deployMutation.isPending && deployingId === service.id}
              onClick={() => handleDeploy(service)}
            >
              {deployMutation.isPending && deployingId === service.id ? "Deploying…" : "Deploy"}
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
